import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomDropdown from './CustomDropdown';
import { VendorCombobox } from './VendorCombobox';
import { AiSuggestedBadge } from './import/AiSuggestedBadge';
import { formatSpecifications } from '../utils/importReviewGrouping';
import { AI_HIGH_CONFIDENCE } from '../utils/aiSuggestionFields';
import {
  buildInitialEnhanceSelection,
  collectApprovedUpdates,
  computeEnhanceApplyStats,
  enhanceProgressPercent,
  formatEnhanceRange,
} from '../utils/enhanceReviewUtils';
import type {
  EnhanceApprovedUpdate,
  EnhanceSession,
  EnhanceSuggestion,
  EnhanceUndoConflict,
} from '../types/enhanceModifications';
import apiService from '../services/ApiService';

export type { EnhanceApprovedUpdate, EnhanceSuggestion };

interface EnhanceModificationsModalProps {
  isOpen: boolean;
  vehicleId: number;
  onClose: () => void;
  onApplied?: () => void;
}

type FieldKey =
  | 'title'
  | 'category'
  | 'vendor'
  | 'description'
  | 'specifications'
  | 'group_label'
  | 'parent';
type UiPhase =
  | 'loading'
  | 'resume'
  | 'ready'
  | 'analyzing'
  | 'review'
  | 'applying'
  | 'batch_done'
  | 'complete'
  | 'undo_conflict';

const FIELD_LABELS: Record<FieldKey, string> = {
  title: 'Part name',
  category: 'Category',
  vendor: 'Brand',
  description: 'Description',
  specifications: 'Specifications',
  group_label: 'Group',
  parent: 'Parent part',
};

const BATCH_SIZE = 50;

function displayValue(field: FieldKey, value: unknown): string {
  if (value == null || value === '') return '-';
  if (field === 'specifications') {
    return formatSpecifications(value as string[] | Record<string, unknown> | string) || '-';
  }
  return String(value);
}

type BatchCache = {
  suggestions: EnhanceSuggestion[];
  selected: Record<string, boolean>;
  edits: Record<string, string>;
  returned: number;
  applied?: boolean;
};

export const EnhanceModificationsModal: React.FC<EnhanceModificationsModalProps> = ({
  isOpen,
  vehicleId,
  onClose,
  onApplied,
}) => {
  const [phase, setPhase] = useState<UiPhase>('loading');
  const [session, setSession] = useState<EnhanceSession | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [returned, setReturned] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [prevOffset, setPrevOffset] = useState<number | null>(null);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<EnhanceSuggestion[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [vendors, setVendors] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [aiSuggested, setAiSuggested] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [lastAppliedCount, setLastAppliedCount] = useState(0);
  const [canUndo, setCanUndo] = useState(false);
  const [conflicts, setConflicts] = useState<EnhanceUndoConflict[]>([]);
  const [showChanges, setShowChanges] = useState(false);
  const [changes, setChanges] = useState<any[]>([]);
  const batchCacheRef = useRef<Record<number, BatchCache>>({});
  const requestLockRef = useRef(false);
  const pendingResumeRef = useRef<EnhanceSession | null>(null);

  const resetLocalReview = useCallback(() => {
    setSuggestions([]);
    setSelected({});
    setEdits({});
    setAiSuggested({});
    setReturned(0);
    setError(null);
  }, []);

  const persistCurrentBatch = useCallback(() => {
    if (!suggestions.length) return;
    batchCacheRef.current[offset] = {
      suggestions,
      selected: { ...selected },
      edits: { ...edits },
      returned,
      applied: batchCacheRef.current[offset]?.applied,
    };
  }, [edits, offset, returned, selected, suggestions]);

  const loadBatchFromCache = useCallback((targetOffset: number): boolean => {
    const cached = batchCacheRef.current[targetOffset];
    if (!cached) return false;
    setOffset(targetOffset);
    setSuggestions(cached.suggestions);
    setSelected(cached.selected);
    setEdits(cached.edits);
    setReturned(cached.returned);
    setAiSuggested(() => {
      const next: Record<string, boolean> = {};
      cached.suggestions.forEach((s) => {
        s.fields.forEach((field) => {
          next[`${s.id}:${field}`] = true;
        });
      });
      return next;
    });
    setPhase(cached.applied ? 'batch_done' : 'review');
    return true;
  }, []);

  const bootstrap = useCallback(async () => {
    setPhase('loading');
    setError(null);
    try {
      const status = await apiService.enhanceSession(vehicleId, 'status');
      const active = status.active_session as EnhanceSession | null | undefined;
      setTotal(status.total_modifications ?? 0);
      if (active && active.status === 'active') {
        pendingResumeRef.current = active;
        setSession(active);
        setOffset(active.current_offset || 0);
        setPhase('resume');
        return;
      }
      pendingResumeRef.current = null;
      setSession(null);
      setOffset(0);
      resetLocalReview();
      setPhase(status.total_modifications === 0 ? 'ready' : 'ready');
    } catch (err: any) {
      setError(err.message || 'Could not open enhancement');
      setPhase('ready');
    }
  }, [resetLocalReview, vehicleId]);

  useEffect(() => {
    if (!isOpen) return;
    batchCacheRef.current = {};
    requestLockRef.current = false;
    setCanUndo(false);
    setConflicts([]);
    setShowChanges(false);
    setChanges([]);
    setLastAppliedCount(0);
    void bootstrap();
  }, [bootstrap, isOpen]);

  const analyzeAt = useCallback(
    async (targetOffset: number, sessionToken?: string, force = false) => {
      if (requestLockRef.current) return;
      if (!force && loadBatchFromCache(targetOffset)) {
        const cached = batchCacheRef.current[targetOffset];
        if (cached && !cached.applied) return;
      }

      requestLockRef.current = true;
      setPhase('analyzing');
      setError(null);
      try {
        const data = await apiService.enhanceModificationsAnalyze(vehicleId, {
          offset: targetOffset,
          limit: BATCH_SIZE,
          session_token: sessionToken || session?.session_token,
        });
        setSession(data.session || session);
        setTotal(data.total);
        setOffset(data.offset);
        setReturned(data.returned);
        setHasMore(Boolean(data.has_more));
        setPrevOffset(data.previous_offset);
        setNextOffset(data.next_offset);
        setCategories(data.categories || []);
        setVendors((data.vendors || []).map(String));
        const nextSuggestions = data.suggestions || [];
        setSuggestions(nextSuggestions);
        const initial = buildInitialEnhanceSelection(nextSuggestions, AI_HIGH_CONFIDENCE);
        setSelected(initial);
        setEdits({});
        const suggestedFlags: Record<string, boolean> = {};
        nextSuggestions.forEach((s: EnhanceSuggestion) => {
          s.fields.forEach((field: string) => {
            suggestedFlags[`${s.id}:${field}`] = true;
          });
        });
        setAiSuggested(suggestedFlags);
        batchCacheRef.current[data.offset] = {
          suggestions: nextSuggestions,
          selected: initial,
          edits: {},
          returned: data.returned,
        };
        if (data.total === 0) {
          setPhase('ready');
        } else if (nextSuggestions.length === 0) {
          setPhase('review');
          setError('No improvements suggested for this group. Continue to the next when ready.');
        } else {
          setPhase('review');
        }
      } catch (err: any) {
        setError(err.message || 'Analysis failed. You can retry this group.');
        setPhase('ready');
      } finally {
        requestLockRef.current = false;
      }
    },
    [loadBatchFromCache, session, vehicleId]
  );

  const handleResume = async () => {
    const active = pendingResumeRef.current || session;
    if (!active) return;
    setSession(active);
    pendingResumeRef.current = null;
    await analyzeAt(active.current_offset || 0, active.session_token, true);
  };

  const handleStartOver = async () => {
    if (requestLockRef.current) return;
    requestLockRef.current = true;
    setError(null);
    try {
      const data = await apiService.enhanceSession(vehicleId, 'start_over');
      batchCacheRef.current = {};
      setSession(data.session);
      setCanUndo(false);
      pendingResumeRef.current = null;
      requestLockRef.current = false;
      await analyzeAt(0, data.session?.session_token, true);
    } catch (err: any) {
      setError(err.message || 'Could not start over');
      setPhase('resume');
      requestLockRef.current = false;
    }
  };

  const handleDiscard = async () => {
    if (!session?.session_token) {
      onClose();
      return;
    }
    try {
      await apiService.enhanceSession(vehicleId, 'discard', session.session_token);
    } catch {
      // still close
    }
    onClose();
  };

  const handleStartAnalyze = async () => {
    if (requestLockRef.current) return;
    try {
      const created = await apiService.enhanceSession(vehicleId, 'create');
      setSession(created.session);
      await analyzeAt(0, created.session?.session_token, true);
    } catch (err: any) {
      setError(err.message || 'Could not start enhancement');
    }
  };

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  const rangeLabel = useMemo(
    () => formatEnhanceRange(offset, returned || suggestions.length || 0, total),
    [offset, returned, suggestions.length, total]
  );

  const progress = useMemo(
    () => enhanceProgressPercent(Math.min(offset + (returned || 0), total), total),
    [offset, returned, total]
  );

  const remaining = Math.max(0, total - Math.min(offset + (returned || 0), total));

  const editKey = (id: number, field: string) => `${id}:${field}:edit`;

  const getSuggestedDisplay = (s: EnhanceSuggestion, field: FieldKey): string => {
    const edited = edits[editKey(s.id, field)];
    if (edited !== undefined) return edited;
    if (field === 'parent') {
      const pid = s.suggested.parent_modification_id;
      if (pid == null) return '';
      const parent = suggestions.find((x) => x.id === pid);
      return parent ? `${parent.title} (#${pid})` : `#${pid}`;
    }
    const raw = (s.suggested as Record<string, unknown>)[field];
    if (field === 'specifications') {
      return formatSpecifications((raw as string[] | null) || undefined) || '';
    }
    return raw == null ? '' : String(raw);
  };

  const markEdited = (id: number, field: FieldKey, value: string) => {
    setEdits((prev) => ({ ...prev, [editKey(id, field)]: value }));
    setAiSuggested((prev) => ({ ...prev, [`${id}:${field}`]: false }));
  };

  const handleApply = async () => {
    if (requestLockRef.current || !session?.session_token) return;
    persistCurrentBatch();
    const updates = collectApprovedUpdates(suggestions, selected, edits);
    if (!updates.length) return;

    requestLockRef.current = true;
    setPhase('applying');
    setError(null);
    try {
      const stats = computeEnhanceApplyStats(suggestions, selected, edits);
      const result = await apiService.enhanceModificationsApply(vehicleId, updates, {
        session_token: session.session_token,
        batch_offset: offset,
        stats,
      });
      setLastAppliedCount(result.applied_count);
      setCanUndo(Boolean(result.can_undo ?? true));
      if (result.session) setSession(result.session);
      batchCacheRef.current[offset] = {
        ...(batchCacheRef.current[offset] || {
          suggestions,
          selected,
          edits,
          returned,
        }),
        applied: true,
      };
      setPhase('batch_done');
      onApplied?.();
    } catch (err: any) {
      setError(err.message || 'Could not apply selected improvements');
      setPhase('review');
    } finally {
      requestLockRef.current = false;
    }
  };

  const handleSkipBatch = () => {
    persistCurrentBatch();
    if (batchCacheRef.current[offset]) {
      batchCacheRef.current[offset].applied = true;
    }
    setPhase('batch_done');
  };

  const goNext = async () => {
    persistCurrentBatch();
    if (nextOffset == null && !hasMore) {
      await finishSession();
      return;
    }
    const target = nextOffset ?? offset + BATCH_SIZE;
    await analyzeAt(target, session?.session_token);
  };

  const goPrevious = async () => {
    persistCurrentBatch();
    const target = prevOffset != null ? prevOffset : Math.max(0, offset - BATCH_SIZE);
    if (loadBatchFromCache(target)) {
      setHasMore(target + BATCH_SIZE < total);
      setPrevOffset(target > 0 ? Math.max(0, target - BATCH_SIZE) : null);
      setNextOffset(target + BATCH_SIZE < total ? target + BATCH_SIZE : null);
      return;
    }
    await analyzeAt(target, session?.session_token, true);
  };

  const finishSession = async () => {
    if (!session?.session_token) {
      setPhase('complete');
      return;
    }
    try {
      const data = await apiService.enhanceSession(vehicleId, 'complete', session.session_token);
      if (data.session) setSession(data.session);
    } catch {
      // still mark complete locally
    }
    setPhase('complete');
  };

  const handleUndo = async (skipConflicts = false) => {
    if (requestLockRef.current || !session?.session_token) return;
    requestLockRef.current = true;
    setError(null);
    try {
      const result = await apiService.enhanceModificationsUndo(vehicleId, {
        session_token: session.session_token,
        skip_conflicts: skipConflicts,
      });
      if (result.requires_review && result.conflicts?.length) {
        setConflicts(result.conflicts);
        setPhase('undo_conflict');
        return;
      }
      setCanUndo(false);
      setConflicts([]);
      setLastAppliedCount(0);
      if (result.session) setSession(result.session);
      onApplied?.();
      setPhase(hasMore || suggestions.length ? 'batch_done' : 'complete');
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Could not undo enhancements');
    } finally {
      requestLockRef.current = false;
    }
  };

  const handleViewChanges = async () => {
    if (!session?.session_token) return;
    try {
      const result = await apiService.enhanceModificationsUndo(vehicleId, {
        session_token: session.session_token,
        list_changes: true,
      });
      setChanges(result.changes || []);
      setShowChanges(true);
    } catch (err: any) {
      setError(err.message || 'Could not load changes');
    }
  };

  if (!isOpen) return null;

  const toggleAll = (value: boolean) => {
    const next: Record<string, boolean> = {};
    suggestions.forEach((s) => {
      s.fields.forEach((field) => {
        next[`${s.id}:${field}`] = value;
      });
    });
    setSelected(next);
  };

  const busy = phase === 'analyzing' || phase === 'applying' || phase === 'loading';

  return (
    <div className="modal-overlay">
      <div className="modal-sheet w-full max-w-[390px] sm:max-w-3xl flex flex-col">
        <div className="px-4 py-3 border-b border-white/[0.08] flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="heading-card">Improve existing parts</h2>
            <p className="text-muted text-xs">
              Review suggestions before anything changes. Work in groups of {BATCH_SIZE}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white px-2 py-2 min-w-[44px] min-h-[44px]"
            aria-label="Close"
            disabled={phase === 'applying'}
          >
            ✕
          </button>
        </div>

        {total > 0 && phase !== 'resume' && phase !== 'loading' && (
          <div className="px-4 py-2 border-b border-white/[0.06] space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
              <span>{rangeLabel}</span>
              <span>{progress}% · {remaining} remaining</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#141414] overflow-hidden">
              <div
                className="h-full bg-orange-500/80 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {error && (
            <div className="rounded-lg border border-red-700/50 bg-red-900/20 text-red-200 text-sm px-3 py-2">
              {error}
              {(phase === 'ready' || phase === 'review') && session && (
                <button
                  type="button"
                  className="block mt-2 text-orange-400 underline text-xs"
                  onClick={() => analyzeAt(offset, session.session_token, true)}
                >
                  Retry this group
                </button>
              )}
            </div>
          )}

          {phase === 'loading' && (
            <p className="text-center py-10 text-gray-400 text-sm">Checking for saved progress…</p>
          )}

          {phase === 'resume' && session && (
            <div className="space-y-3 py-4">
              <p className="text-sm text-gray-200">
                You have an enhancement in progress ({session.processed_items || 0} of{' '}
                {session.total_items} modifications).
              </p>
              <div className="flex flex-col gap-2">
                <button type="button" className="btn-primary min-h-[44px]" onClick={handleResume}>
                  Resume
                </button>
                <button
                  type="button"
                  className="btn-secondary min-h-[44px]"
                  onClick={handleStartOver}
                >
                  Start over
                </button>
                <button
                  type="button"
                  className="min-h-[44px] text-red-300 text-sm"
                  onClick={handleDiscard}
                >
                  Discard session
                </button>
              </div>
            </div>
          )}

          {phase === 'ready' && total === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">
              No modifications yet. Import or add parts first.
            </div>
          )}

          {phase === 'ready' && total > 0 && (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-muted">
                Improve categories, brands, descriptions, and specs on {total} modifications.
              </p>
              <p className="text-xs text-muted opacity-80">Suggestions only. You approve every change.</p>
              <button
                type="button"
                className="btn-primary min-h-[44px] px-4"
                onClick={handleStartAnalyze}
                disabled={busy}
              >
                Review first {Math.min(BATCH_SIZE, total)}
              </button>
            </div>
          )}

          {phase === 'analyzing' && (
            <p className="text-center py-10 text-gray-300 text-sm">Reviewing suggestions…</p>
          )}

          {(phase === 'review' || phase === 'applying') && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => analyzeAt(offset, session?.session_token, true)}
                  disabled={busy}
                  className="text-xs px-3 py-2 min-h-[40px] rounded border border-white/[0.10] text-gray-300 disabled:opacity-50"
                >
                  Re-analyze this group
                </button>
                {suggestions.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleAll(true)}
                      className="text-xs px-3 py-2 min-h-[40px] rounded border border-white/[0.10] text-gray-300"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleAll(false)}
                      className="text-xs px-3 py-2 min-h-[40px] rounded border border-white/[0.10] text-gray-300"
                    >
                      Deselect all
                    </button>
                    <span className="text-xs text-gray-500 w-full sm:w-auto sm:ml-auto">
                      {suggestions.length} with suggestions · {selectedCount} selected
                    </span>
                  </>
                )}
              </div>

              {suggestions.map((s) => (
                <div
                  key={s.id}
                  className="form-section !p-3 space-y-3"
                >
                  <div>
                    <h3 className="text-sm font-medium text-white">{s.title}</h3>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Confidence {Math.round(s.confidence * 100)}%
                      {s.confidence >= AI_HIGH_CONFIDENCE ? ' · high confidence pre-selected' : ''}
                    </p>
                  </div>

                  {(s.fields as FieldKey[]).map((field) => {
                    const key = `${s.id}:${field}`;
                    const currentRaw =
                      field === 'parent'
                        ? s.current.parent_modification_id
                        : field === 'title'
                          ? (s.current.title ?? s.title)
                          : (s.current as Record<string, unknown>)[field];
                    const current =
                      field === 'parent' && currentRaw
                        ? (() => {
                            const pid = Number(currentRaw);
                            const parent = suggestions.find((x) => x.id === pid);
                            return parent ? `${parent.title} (#${pid})` : `#${pid}`;
                          })()
                        : displayValue(field, currentRaw);
                    const suggestedText = getSuggestedDisplay(s, field);
                    const categoryOptions = categories.map((c) => ({ value: c, label: c }));

                    return (
                      <div key={key} className="rounded-md border border-white/[0.07] bg-[#0c0c0c]/50 p-2.5">
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            checked={Boolean(selected[key])}
                            onChange={(e) =>
                              setSelected((prev) => ({ ...prev, [key]: e.target.checked }))
                            }
                            className="rounded border-white/[0.14] text-orange-500 focus:ring-orange-500 w-4 h-4"
                          />
                          <span className="text-xs font-medium text-gray-200">
                            {FIELD_LABELS[field]}
                          </span>
                          <AiSuggestedBadge show={Boolean(aiSuggested[key])} />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-gray-500 mb-1">Current</p>
                            <p className="text-gray-300 whitespace-pre-wrap break-words min-h-[1.5rem]">
                              {current}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 mb-1">Suggested</p>
                            {field === 'category' ? (
                              <CustomDropdown
                                value={
                                  categoryOptions.some((o) => o.value === suggestedText)
                                    ? suggestedText
                                    : categories[0] || ''
                                }
                                onChange={(value) => markEdited(s.id, field, value)}
                                options={categoryOptions}
                                disabled={!selected[key] || phase === 'applying'}
                              />
                            ) : field === 'vendor' ? (
                              <VendorCombobox
                                value={suggestedText}
                                onChange={(value) => markEdited(s.id, field, value)}
                                vendors={vendors}
                                disabled={!selected[key] || phase === 'applying'}
                                className="text-xs py-1.5"
                                placeholder="Search or add brand…"
                              />
                            ) : field === 'description' || field === 'specifications' ? (
                              <textarea
                                value={suggestedText}
                                disabled={!selected[key] || phase === 'applying'}
                                onChange={(e) => markEdited(s.id, field, e.target.value)}
                                rows={field === 'specifications' ? 3 : 2}
                                placeholder={
                                  field === 'specifications'
                                    ? 'Add one detail per line'
                                    : undefined
                                }
                                className="input text-xs !min-h-[36px] !py-1.5 !px-2"
                              />
                            ) : (
                              <input
                                type="text"
                                value={suggestedText}
                                disabled={!selected[key] || phase === 'applying'}
                                onChange={(e) => markEdited(s.id, field, e.target.value)}
                                className="input text-xs !min-h-[36px] !py-1.5 !px-2"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}

          {phase === 'batch_done' && (
            <div className="space-y-3 py-4">
              <p className="text-sm text-emerald-300 font-medium">
                {lastAppliedCount > 0
                  ? 'Enhancements applied'
                  : 'Group skipped. Nothing changed'}
              </p>
              {canUndo && lastAppliedCount > 0 && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    className="btn-secondary min-h-[44px]"
                    onClick={() => handleUndo(false)}
                  >
                    Undo enhancements
                  </button>
                  <button
                    type="button"
                    className="btn-ghost min-h-[44px] text-sm"
                    onClick={handleViewChanges}
                  >
                    View changes
                  </button>
                </div>
              )}
              {showChanges && (
                <div className="max-h-40 overflow-y-auto text-xs text-gray-400 border border-white/[0.08] rounded p-2 space-y-1">
                  {changes.length === 0 && <p>No recorded changes.</p>}
                  {changes.slice(0, 50).map((c) => (
                    <p key={c.id}>
                      #{c.modification_id} {c.title || ''} · {c.field_name}
                      {c.reverted_at ? ' (undone)' : ''}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {phase === 'undo_conflict' && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-amber-200">
                Some fields were edited after enhancement. Those will not be overwritten.
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2 text-xs">
                {conflicts.map((c, i) => (
                  <div key={`${c.modification_id}-${c.field}-${i}`} className="border border-amber-700/40 rounded p-2">
                    <p className="text-white">{c.title || `Mod #${c.modification_id}`} · {c.field}</p>
                    <p className="text-gray-400">Current: {c.current_value || '-'}</p>
                    <p className="text-gray-500">Was enhanced to: {c.expected_new_value || '-'}</p>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn-primary min-h-[44px] w-full"
                onClick={() => handleUndo(true)}
              >
                Undo fields that still match
              </button>
              <button
                type="button"
                className="btn-secondary min-h-[44px] w-full"
                onClick={() => setPhase('batch_done')}
              >
                Keep current values
              </button>
            </div>
          )}

          {phase === 'complete' && (
            <div className="text-center py-10 space-y-2">
              <p className="text-sm text-emerald-300 font-medium">All groups reviewed</p>
              <p className="text-xs text-gray-400">You reviewed all modification groups.</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 px-4 py-3 border-t border-white/[0.08] bg-[#0a0a0a] flex flex-col sm:flex-row gap-2 sm:justify-end">
          {(phase === 'review' || phase === 'applying') && (
            <>
              <button
                type="button"
                onClick={handleSkipBatch}
                className="btn-secondary min-h-[44px]"
                disabled={busy}
              >
                Skip this group
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={busy || selectedCount === 0}
                className="btn-primary min-h-[44px] disabled:opacity-50"
              >
                {phase === 'applying'
                  ? 'Applying…'
                  : `Apply selected improvements${selectedCount ? ` (${selectedCount})` : ''}`}
              </button>
            </>
          )}

          {phase === 'batch_done' && (
            <>
              <button
                type="button"
                onClick={goPrevious}
                disabled={offset <= 0 || busy}
                className="btn-secondary min-h-[44px] disabled:opacity-40"
              >
                Previous group
              </button>
              {hasMore || nextOffset != null ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={busy}
                  className="btn-primary min-h-[44px]"
                >
                  Analyze next {Math.min(BATCH_SIZE, remaining || BATCH_SIZE)}
                </button>
              ) : (
                <button type="button" onClick={finishSession} className="btn-primary min-h-[44px]">
                  Finish
                </button>
              )}
            </>
          )}

          {(phase === 'complete' || phase === 'ready') && (
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary min-h-[44px]"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
